import { progress } from "@/app/_utils/components/NavigationProgress";
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { useRouter as useNextRouter } from "next/navigation";
import { useMemo } from "react";

/**
 * Returns true when `href` resolves to the same pathname + search as the
 * current location.  Mirrors the same-URL guard in `shouldTrackAnchor`
 * inside `NavigationProgress`.
 *
 * @param href - The navigation target (absolute or relative URL).
 * @returns Whether the target matches the current route.
 */
function isSameRoute(href: string): boolean {
  try {
    const target = new URL(href, window.location.href);

    return (
      target.pathname === window.location.pathname &&
      target.search === window.location.search
    );
  } catch {
    return false;
  }
}

/**
 * Wraps Next.js `useRouter` with progress bar support for programmatic navigation.
 *
 * The `NavigationProgress` component automatically detects `<Link>` / `<a>` clicks
 * via event delegation, but `router.push()` / `router.replace()` bypass the DOM
 * so the progress bar wouldn't start. This hook bridges that gap by calling
 * `progress.start()` before delegating to the native router.
 *
 * `progress.done()` is handled automatically by the `pushState` / `replaceState`
 * proxy installed by `NavigationProgress`.
 *
 * Same-URL navigation (e.g. `push("/")` while already on `/`) skips the
 * progress bar because `pushState` is never called and `done()` would never fire.
 * Use `refresh()` alongside the push to show a progress bar in that scenario —
 * Next.js calls `replaceState` once the server component refetch settles, which
 * triggers `done()`.
 *
 * @returns A router instance with progress bar support on `push`, `replace`, `back`, and `refresh`.
 */
export function useProgressRouter(): AppRouterInstance {
  const router = useNextRouter();

  return useMemo(
    () => ({
      ...router,
      push: (...args: Parameters<AppRouterInstance["push"]>) => {
        if (!isSameRoute(args[0])) {
          progress.start();
        }
        router.push(...args);
      },
      replace: (...args: Parameters<AppRouterInstance["replace"]>) => {
        if (!isSameRoute(args[0])) {
          progress.start();
        }
        router.replace(...args);
      },
      back: () => {
        progress.start();
        router.back();
      },
      refresh: () => {
        progress.start();
        router.refresh();
      },
    }),
    [router],
  );
}
